import { useEffect, useState } from 'react';
import { Globe2 } from 'lucide-react';
import Button from '../ui/Button';
import { GrowingRegionPicker } from '../cultivar';
import { byCountryThenName } from '../cultivar/growingRegionDraft';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import type { Farm } from '../../types/orchard';
import type { GrowingRegion } from '../../types/cultivar';

interface Props {
  farm: Farm;
  /** Called after a save so the page reloads the farm and its region name. */
  onSaved: () => void;
  /** Hide the edit affordance (a super admin looking at another org's farm). */
  readOnly?: boolean;
}

/**
 * The farm's growing region, set or changed where it is shown. Evaluations at
 * the farm inherit it, so a blank one is the reason the farm's maturity dates
 * are not reaching the cultivar — and the fix belongs next to the explanation,
 * not behind the whole Edit form.
 */
function FarmRegionEditor({ farm, onSaved, readOnly }: Props) {
  const [editing, setEditing] = useState(false);
  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [value, setValue] = useState<number>(farm.growing_region_id ?? 0);
  const [nearest, setNearest] = useState<{ region: GrowingRegion; distance_km: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    orchardService.listGrowingRegions()
      .then((res) => { if (!cancelled && res.success && res.data) setRegions(res.data); })
      .catch(() => {});
    const c = farm.center_point;
    if (c && !farm.growing_region_id) {
      orchardService.findNearestRegion(c.lat, c.lng)
        .then((res) => { if (!cancelled && res.success) setNearest(res.data ?? null); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [editing, farm.center_point, farm.growing_region_id]);

  const open = () => {
    setValue(farm.growing_region_id ?? 0);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // The farm PUT validates name and boundary as required, so they ride along unchanged.
      const res = await orchardService.updateFarm(farm.id, {
        name: farm.name,
        boundary_geometry: farm.boundary_geometry,
        growing_region_id: value || null,
      });
      if (!res.success) throw new Error(res.error?.message || 'Could not save the region');
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the region'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-600 mb-2">
        <Globe2 className="h-4 w-4 text-neutral-400 flex-shrink-0" />
        <span className="min-w-0 break-words">
          {farm.growing_region_name
            ? <>Growing region: {farm.growing_region_name}</>
            : <span className="text-neutral-400">No growing region set</span>}
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={open}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {farm.growing_region_id ? 'Change' : 'Set region'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-3 space-y-2 rounded-lg border border-neutral-200 p-3">
      {error && <div className="p-2 rounded bg-error-50 text-error-800 text-xs">{error}</div>}
      <GrowingRegionPicker
        regions={regions}
        value={value}
        onChange={setValue}
        onCreated={(r) => setRegions((prev) => [...prev, r].sort(byCountryThenName))}
        createRegion={(payload) => orchardService.createGrowingRegion(payload)}
        label="Growing region"
        disabled={saving}
      />
      {nearest && !value && (
        <button
          type="button"
          onClick={() => setValue(nearest.region.id)}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium text-left"
        >
          Nearest region to this farm is {nearest.region.name} ({nearest.distance_km} km away) — use it
        </button>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} isLoading={saving}>Save region</Button>
      </div>
    </div>
  );
}

export default FarmRegionEditor;
